/**
 * Per-installation device identifier for license-binding.
 *
 * Generated lazily on first call via WebCrypto and persisted in localStorage.
 * Sent to /license/issue + /license/refresh so the server can embed it in the
 * JWT (`deviceId` claim) — that enables per-device JTI revocation lists later
 * without forcing all users to re-sign-in.
 *
 * Notes:
 * - localStorage survives PWA reloads + offline cache flushes; only a manual
 *   "Clear site data" wipes it. That's acceptable for our threat model.
 * - The id is opaque (UUID v4 from `crypto.randomUUID`). It is not tied to
 *   hardware fingerprints — those are unreliable in a browser sandbox.
 */
const DEVICE_ID_KEY = "coach.device.id";

export function getOrCreateDeviceId(): string {
  // SSR / non-browser fallback: return a stable sentinel so the server can
  // still issue a token, just without device-binding.
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return "";
  }
  let existing: string | null = null;
  try {
    existing = localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    // Storage disabled (private mode) — degrade gracefully.
    return "";
  }
  if (existing && existing.length >= 8) return existing;

  const fresh =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : // Fallback for very old runtimes — 16 random hex bytes.
        Array.from({ length: 16 }, () =>
          Math.floor(Math.random() * 256)
            .toString(16)
            .padStart(2, "0"),
        ).join("");
  try {
    localStorage.setItem(DEVICE_ID_KEY, fresh);
  } catch {
    /* ignore — caller still gets a valid id for this session */
  }
  return fresh;
}
